import { useEffect } from "react";
import { Plus, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import { inrFull } from "../../lib/format";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export const n = (v) => Number(v) || 0;

export const Match = ({ ok, okText, badText, testid }) => ok ? (
  <div className="flex items-center gap-2 text-green-700 text-sm font-semibold" data-testid={`${testid}-ok`}><CheckCircle2 className="w-4 h-4" /> {okText}</div>
) : (
  <div className="flex items-center gap-2 text-red-700 text-sm font-semibold" data-testid={`${testid}-error`}><AlertCircle className="w-4 h-4 shrink-0" /> {badText}</div>
);

const Txt = ({ label, ...p }) => (
  <label className="block"><span className="text-xs font-semibold text-slate-600">{label}</span><Input {...p} className={`mt-1 h-11 ${p.className || ""}`} /></label>
);

export function DetailList({ title, rows, setRows, total, blank, fields, sumLabel, mismatch, testid, children }) {
  useEffect(() => {
    if (total > 0 && !rows.length) setRows([{ ...blank(), amount: String(total) }]);
    if (total <= 0 && rows.length && rows.every((r) => !r.id && !r.customer_name)) setRows([]);
  }, [total]); // eslint-disable-line react-hooks/exhaustive-deps
  if (total <= 0) return null;
  const sum = rows.reduce((a, r) => a + n(r.amount), 0);
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="bs-card p-4 space-y-3 border-[#E6D9B8] bg-[#FFFDF8]" data-testid={testid}>
      <div className="flex items-center justify-between"><div className="bs-eyebrow">{title}</div><div className="text-sm font-semibold text-[#011E38]">Today: {inrFull(total)}</div></div>
      {rows.map((r, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`${testid}-row-${i}`}>
          <div className="flex items-center justify-between"><span className="text-xs font-bold text-slate-500">#{i + 1}</span>
            {rows.length > 1 && <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-red-600" data-testid={`${testid}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {fields.map(([k, label, type, extra]) => k === "_custom" ? <div key={label} className="col-span-2 empty:hidden">{extra(r, (kk, v) => upd(i, kk, v))}</div> : (
              <Txt key={k} label={label} type={type || "text"} value={r[k] ?? ""} onChange={(e) => upd(i, k, e.target.value)} data-testid={`${testid}-${k}-${i}`} {...(extra || {})} />
            ))}
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setRows([...rows, blank()])} data-testid={`${testid}-add`}><Plus className="w-4 h-4 mr-1" /> Add another</Button>
      <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm"><span>{sumLabel}</span><b className="num" data-testid={`${testid}-total`}>{inrFull(sum)}</b></div>
      <Match ok={Math.round(sum) === Math.round(total)} okText="Details match today's amount" badText={mismatch} testid={testid} />
      {children}
    </div>
  );
}

export const detailsComplete = (rows, keys) => rows.every((r) => keys.every((k) => String(r[k] ?? "").trim()) && n(r.amount) > 0);
